import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { logoutApi } from '../../services/authService';
import { getProfile, newGetNotifications } from '../../services/userService';
import { useNotification } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionsContext';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';

const DefaultProfile = '/images/profile/default-profile.png';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const { permissions } = usePermissions();
  const { unreadCount, updateUnreadCount } = useNotification();

  const [profileData, setProfileData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const isActive = (path) => {
    const search = location.search || '';
    if (path === '/admin/courses') {
      return (
        (location.pathname.startsWith('/admin/courses') ||
        location.pathname.includes('/analytics/') ||
        location.pathname.includes('CourseAnalyticsSurveyDetails') ||
        location.pathname.includes('CourseAnalyticsAssessmentDetails') ||
        location.pathname.includes('edit-create')) &&
        !search.includes('tab=certificates')
      );
    }
    if (path === '/admin/surveys') {
      return location.pathname.startsWith('/admin/surveys');
    }
    if (path === '/admin/courses?tab=certificates') {
      return location.pathname === '/admin/courses' && search.includes('tab=certificates');
    }
    if (path === '/admin/live-sessions') {
      return (
        location.pathname.startsWith('/admin/classrooms') ||
        location.pathname.startsWith('/admin/learning-spaces') ||
        location.pathname.startsWith('/admin/live-sessions') ||
        location.pathname.includes('classroom-dashboard') ||
        location.pathname.includes('ClassroomAssignmentDetails') ||
        location.pathname.includes('ClassroomAssessmentDetails') ||
        location.pathname.includes('/trainees/')
      );
    }
    if (path === '/admin/roles-and-access') {
      return location.pathname.startsWith('/admin/roles-and-access') || location.pathname.startsWith('/admin/trainees') || location.pathname.includes('traineesAnalytics');
    }
    return location.pathname === path;
  };

  const navItems = [
    permissions.dashboard_management_view && { label: 'Dashboard', path: '/admin/dashboard', icon: 'fluent:grid-20-regular', iconFilled: 'fluent:grid-20-filled' },
    permissions.course_management_view && { label: 'Courses Console', path: '/admin/courses', icon: 'fluent:book-20-regular', iconFilled: 'fluent:book-20-filled' },
    permissions.course_management_view && { label: 'Surveys Console', path: '/admin/surveys', icon: 'fluent:clipboard-task-list-ltr-20-regular', iconFilled: 'fluent:clipboard-task-list-ltr-20-filled' },
    permissions.classroom_management_view && { label: 'Live Console', path: '/admin/live-sessions', icon: 'fluent:video-person-20-regular', iconFilled: 'fluent:video-person-20-filled' },
    permissions.course_approval_workflow_view && { label: 'Admin Console', path: '/admin/approvalRequest', icon: 'fluent:clipboard-task-list-20-regular', iconFilled: 'fluent:clipboard-task-list-20-filled' },
    // permissions.certificate_management_view && { label: 'Certificates', path: '/admin/courses?tab=certificates', icon: 'fluent:certificate-20-regular', iconFilled: 'fluent:certificate-20-filled' },
    (permissions.trainee_management_view || permissions.roles_and_access_user_view) && { label: 'Access Management', path: '/admin/roles-and-access', icon: 'fluent:shield-lock-20-regular', iconFilled: 'fluent:shield-lock-20-filled' },
    permissions.setting_management_view && { label: 'Documents', path: '/admin/organization-docs', icon: 'fluent:document-20-regular', iconFilled: 'fluent:document-20-filled' },
    // permissions.setting_management_view && { label: 'Settings', path: '/admin/settings', icon: 'fluent:settings-20-regular', iconFilled: 'fluent:settings-20-filled' },
  ].filter(Boolean);

  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      setProfileData(response?.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await newGetNotifications({});
      if (response?.notifications) {
        const unread = response.notifications.filter((n) => n.is_read === 0).length;
        updateUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchNotifications();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
      if (menuRef.current && !menuRef.current.contains(e.target) && !e.target.closest('[data-admin-menu-toggle]')) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logoutApi();
      localStorage.clear();
      window.location.href = '/auth/login';
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      window.location.href = '/auth/login';
    }
  };

  const logoInfo = getLogoByDomainAndAlt();
  const logoUrl = logoInfo.darkLogo || logoInfo.logo;

  const getAccountTypeLabel = () => {
    const role = (typeof window !== 'undefined' && localStorage.getItem('role')) || '';
    switch (role.toLowerCase()) {
      case 'admin':
      case 'superadmin':
        return 'Admin';
      case 'trainer':
        return 'Trainer';
      case 'teacher':
        return 'Teacher';
      case 'trainee':
      case 'learner':
        return 'Learner';
      default:
        return '';
    }
  };

  const getDisplayName = () => {
    if (!profileData) return '';
    return (
      profileData.name ||
      profileData.fullName ||
      profileData.full_name ||
      profileData.username ||
      profileData.user_name ||
      profileData.email ||
      ''
    );
  };

  const accountTypeLabel = getAccountTypeLabel();
  const displayName = getDisplayName();

  const renderDesktopHeader = () => (
    <div className="flex flex-col lg:grid lg:grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-6">
      <div className="flex w-full items-center justify-between lg:contents">
        <div className="shrink-0">
          <Link to="/admin/courses" className="inline-block">
            <img
              src={logoUrl}
              alt={logoInfo.alt}
              className="h-5 sm:h-6 md:h-7 lg:h-8 w-auto max-h-8 max-w-[120px] sm:max-w-[140px] lg:max-w-[160px] object-contain"
            />
          </Link>
        </div>

        <div className="hidden md:flex justify-center min-w-0 flex-1 overflow-hidden">
          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-2xl border border-black/[0.08] bg-white overflow-x-auto max-w-full scrollbar-thin">
            <nav className="inline-flex items-center gap-1">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`inline-flex items-center ${isTablet ? 'px-2.5 py-1 text-[12px]' : 'px-3 xl:px-3.5 py-1 text-[13px]'} rounded-[10px] font-medium no-underline transition-all duration-200 whitespace-nowrap shrink-0 focus:outline-none focus-visible:outline-none border ${
                      active ? 'text-white bg-primary border-primary hover:text-white' : 'text-gray-600 bg-transparent hover:bg-transparent hover:text-primary border-transparent hover:border-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 justify-end shrink-0">
          {permissions.notification_management_view && (
            <Link
              to="/admin/notifications"
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 no-underline transition-colors hover:bg-gray-200 relative"
            >
              <Icon icon="fluent:alert-20-regular" width="20" height="20" className="text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          )}

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              className="flex items-center gap-1.5 bg-white px-1.5 py-1 rounded-xl cursor-pointer border border-black/[0.08] hover:border-gray-100 transition"
            >
              <img
                src={profileData?.profile_pic_url || DefaultProfile}
                alt="User"
                className="w-6 h-6 rounded-full object-cover"
              />
              {(displayName || accountTypeLabel) && (
                <div className="hidden sm:flex flex-col items-start max-w-[160px]">
                  {displayName && (
                    <span className="text-[13px] font-medium text-gray-700 leading-tight truncate w-full">
                      {displayName}
                    </span>
                  )}
                  {accountTypeLabel && (
                    <span className=" inline-flex items-center  font-medium text-[10px] text-gray-400  rounded-full">
                      {accountTypeLabel}
                    </span>
                  )}
                </div>
              )}
              <Icon icon={dropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} width="20" height="20" className="text-gray-600" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-[200px] rounded-xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-200 py-2 z-50">
                {permissions.profile_management_view && (
                  <Link to="/admin/profile" className="block px-4 py-2.5 text-sm text-gray-700 no-underline hover:bg-gray-50" onClick={() => setDropdownOpen(false)}>
                    Profile
                  </Link>
                )}
                {permissions.setting_management_view && (
                  <Link to="/admin/settings" className="block px-4 py-2.5 text-sm text-gray-700 no-underline hover:bg-gray-50" onClick={() => setDropdownOpen(false)}>
                    Settings
                  </Link>
                )}
                {permissions.announcement_management_view && (
                  <Link to="/admin/announcement" className="block px-4 py-2.5 text-sm text-gray-700 no-underline hover:bg-gray-50" onClick={() => setDropdownOpen(false)}>
                    Announcement
                  </Link>
                )}
                <div className="h-px bg-gray-200 my-1" />
                <button type="button" onClick={() => { setDropdownOpen(false); handleLogout(); }} className="block px-4 py-2.5 text-sm text-red-600 bg-transparent border-none cursor-pointer w-full text-left hover:bg-red-50">
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderMobileHeader = () => (
    <>
      <div className="flex items-center justify-between w-full">
        <Link to="/admin/courses" className="inline-block shrink-0">
          <img
            src={logoUrl}
            alt={logoInfo.alt}
            className="h-5 w-auto max-h-5 max-w-[120px] object-contain"
          />
        </Link>
        <div className="flex items-center gap-2">
          {permissions.notification_management_view && (
            <Link
              to="/admin/notifications"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 no-underline relative"
            >
              <Icon icon="fluent:alert-20-regular" width="18" height="18" className="text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          )}
          <button
            type="button"
            data-admin-menu-toggle
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="flex items-center justify-center px-3 h-9 rounded-full bg-gray-100 border-none cursor-pointer text-xs font-medium text-gray-700"
            aria-label="Menu"
          >
            {mobileMenuOpen ? 'Close' : 'Menu'}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[998] md:hidden" onClick={() => setMobileMenuOpen(false)} />
          <div ref={menuRef} className="fixed top-0 right-0 bottom-0 w-[280px] max-w-[85vw] bg-white shadow-xl z-[999] md:hidden flex flex-col overflow-hidden animate-slideInRight">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-gray-800">Menu</span>
              <button type="button" onClick={() => setMobileMenuOpen(false)} className="px-3 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-transparent cursor-pointer hover:bg-gray-100 text-xs font-medium text-gray-700" aria-label="Close">
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium no-underline border ${active ? 'bg-primary text-white border-primary hover:text-white' : 'text-gray-700 hover:bg-transparent hover:text-primary border-transparent hover:border-primary'}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="h-px bg-gray-100 my-3" />
              {permissions.profile_management_view && (
                <Link to="/admin/profile" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-sm font-medium text-gray-700 no-underline hover:bg-gray-100">
                  Profile
                </Link>
              )}
              {permissions.setting_management_view && (
                <Link to="/admin/settings" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-sm font-medium text-gray-700 no-underline hover:bg-gray-100">
                  Settings
                </Link>
              )}
              {permissions.announcement_management_view && (
                <Link to="/admin/announcement" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-sm font-medium text-gray-700 no-underline hover:bg-gray-100">
                  Announcement
                </Link>
              )}
              <button type="button" onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="block px-4 py-3 rounded-xl text-sm font-medium text-red-600 bg-transparent border-none cursor-pointer w-full text-left hover:bg-red-50">
                Logout
              </button>
            </div>
          </div>
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            .animate-slideInRight { animation: slideInRight 0.25s ease-out; }
          `}</style>
        </>
      )}
    </>
  );

  return (
    <header className="bg-[#f8f9fb] relative">
      <div className={isMobile ? 'px-4 py-1' : 'px-6 py-1'}>
        {isMobile ? renderMobileHeader() : renderDesktopHeader()}
      </div>
    </header>
  );
}

export default Header;
